import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Gap from '@component/common/gap';
import WeekStamp from '@design-system/week-stamp';
import { ETheme, TIcon } from '@datatype/type';
import { EApiLanguageCode, EApiLanguageLevel, EApiStudyTimeGoal } from '@datatype/iyagigo/api-type';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { useTodayWorksheetWorkbookQuery } from '@hook/iyagigo/useApi';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { useTypedSelector } from '@hook/common/useStore';
import IconTextBlock from '@component/common/block/icon-text-block';
import {
  useHomeAttendanceDayCount,
  useHomeConsecutiveAttendance,
  useHomeWeekAttendance,
} from '@hook/iyagigo/useStudent';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { useStudyConfiguration } from '@hook/iyagigo/useStudent';
import otterReadImage from '@asset/image/otter-read.png';
import ImageBlock from '@component/common/block/image-block';
import Flag from '@component/common/flag';
import Callout from '@design-system/callout';
import Chip from '@design-system/chip';
import { actions as modalActions } from '@store/slice/modal-slice';
import { getStudyRoute } from '@constant/iyagigo/variable';
import { IStudyRouteStep } from '@datatype/iyagigo/type';

type TRouteStepStatus = 'completed' | 'available';
const STUDY_TIME_GOAL_RECOMMENDED_COUNTS: Record<EApiStudyTimeGoal, number> = {
  [EApiStudyTimeGoal.MINIMAL]: 1,
  [EApiStudyTimeGoal.MEDIUM]: 2,
  [EApiStudyTimeGoal.CONSIDERABLE]: 3,
  [EApiStudyTimeGoal.MAXIMAL]: 4,
};

const LANGUAGE_LEVEL_LABELS: Record<EApiLanguageLevel, string> = {
  [EApiLanguageLevel.BEGINNER]: '입문',
  [EApiLanguageLevel.BASIC]: '기초',
  [EApiLanguageLevel.ELEMENTARY]: '초급',
  [EApiLanguageLevel.INTERMEDIATE]: '중급',
  [EApiLanguageLevel.ADVANCED]: '고급',
};

const STUDY_LANGUAGE_LABELS: Record<EApiLanguageCode, string> = {
  [EApiLanguageCode.ENGLISH]: '영어',
  [EApiLanguageCode.JAPANESE]: '일본어',
};

type TStudentStatus = 'guest' | 'free' | 'premium';

// 회원 등급 배지. 등급 표기는 구독 비교표(hub/subscription)의 `Free` / `Plus⁺`와 맞춘다.
const STUDENT_STATUS_BADGE: Record<
  TStudentStatus,
  { icon: TIcon; text: string; textColor: string; backgroundColor: string; borderColor: string }
> = {
  guest: {
    icon: 'ghost',
    text: 'Guest',
    textColor: color.WHITE_1,
    backgroundColor: color.GRAY_1,
    borderColor: color.GRAY_1,
  },
  free: {
    icon: 'smile',
    text: 'Free',
    textColor: color.WHITE_1,
    backgroundColor: color.BLUE_2,
    borderColor: color.BLUE_2,
  },
  // 노랑 채움은 금색으로 읽혀 최상위 등급 신호가 된다.
  premium: {
    icon: 'crown',
    text: 'Plus⁺',
    textColor: color.WHITE_1,
    backgroundColor: color.YELLOW_2,
    borderColor: color.YELLOW_2,
  },
};

const STUDY_TIME_GOAL_LABELS: Record<EApiStudyTimeGoal, string> = {
  [EApiStudyTimeGoal.MINIMAL]: '매일 5분',
  [EApiStudyTimeGoal.MEDIUM]: '매일 10분',
  [EApiStudyTimeGoal.CONSIDERABLE]: '매일 20분',
  [EApiStudyTimeGoal.MAXIMAL]: '매일 30분+',
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.PAGE_PADDING};
`;

const RouteContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_2};
  padding: ${size.CONTENT_PADDING};
`;

const RouteItemContainer = styled.div<{ $status: TRouteStepStatus }>`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
  // margin-top: ${size.MARGIN_1};
  cursor: pointer;
`;

const RouteItemsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  //width: 100%;
`;

const ChipContainer = styled.div`
  z-index: 2;
  background-color: ${color.WHITE_1};
`;

const RouteHeaderContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const RouteRail = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RouteNode = styled.div<{ $status: TRouteStepStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 2px solid ${({ $status }) => ($status === 'available' ? color.GRAY_3 : color.BROWN_1)};
  border-radius: 50%;
  z-index: 1;
  background-color: ${({ $status }) => {
    if ($status === 'completed') return color.BROWN_1;
    return color.WHITE_1;
  }};
`;

const RouteLine = styled.div<{ $isFirst: boolean; $isLast: boolean }>`
  position: absolute;
  top: ${({ $isFirst }) => ($isFirst ? '50%' : `calc(-2 * ${size.MARGIN_1})`)};
  bottom: ${({ $isLast }) => ($isLast ? '50%' : `calc(-2 * ${size.MARGIN_1})`)};
  width: 2px;
  background-color: ${color.GRAY_3};
`;

const RouteContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  padding: ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1};
  background-color: ${color.GRAY_5};
  border-radius: ${size.BORDER_RADIUS_2};
`;

const RouteTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
`;

const WorksheetCard = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_2};
  width: 100%;
  padding: ${size.MARGIN_1};
  background-color: ${color.CYAN_5};
  border: 1px solid ${color.CYAN_4};
  border-radius: ${size.BORDER_RADIUS_2};
  cursor: pointer;
`;

const WorksheetCardContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  flex: 1;
`;

const WorksheetIconContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const flicker = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
  20% {
    opacity: 0.85;
    transform: scale(1.05) rotate(1deg);
  }
  40% {
    opacity: 1;
    transform: scale(0.98) rotate(-2deg);
  }
  60% {
    opacity: 0.9;
    transform: scale(1.03) rotate(2deg);
  }
  80% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
`;

const FireContainer = styled.div`
  width: 100%;

  & svg {
    transform-origin: center bottom;
    animation: ${flicker} 1.2s infinite ease-in-out;
  }
`;

const guestStatusIconWiggle = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-9deg);
  }
  75% {
    transform: rotate(9deg);
  }
`;

const freeStatusIconBounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  45% {
    transform: translateY(-1px) scale(1.02);
  }
  70% {
    transform: translateY(0) scale(0.99);
  }
`;

const premiumStatusIconSparkle = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
    filter: drop-shadow(0 0 0 transparent);
  }
  40% {
    opacity: 0.9;
    transform: scale(1.08);
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.85));
  }
  70% {
    opacity: 1;
    transform: scale(0.99);
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.4));
  }
`;

const StudentStatusIcon = styled.div<{ $status: TStudentStatus }>`
  display: flex;

  & svg {
    transform-origin: center;
    animation-name: ${({ $status }) => {
      if ($status === 'guest') return guestStatusIconWiggle;
      if ($status === 'free') return freeStatusIconBounce;
      return premiumStatusIconSparkle;
    }};
    animation-duration: ${({ $status }) => ($status === 'premium' ? '0.9s' : '0.7s')};
    animation-iteration-count: infinite;
    animation-timing-function: ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    & svg {
      animation: none;
    }
  }
`;

const OtterMessageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

// 왼쪽: 회원 등급 배지 + 함께한 일수 (세로) / 오른쪽: 학습언어 칩
// 위: 등급명 / 아래: 구독하기 (학습언어 칩과 같은 2행 구조)
const StudentStatusBadge = styled.div<{ $backgroundColor: string; $clickable: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  padding: 3px 6px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: ${size.BORDER_RADIUS_1};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const StudentStatusContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const WelcomeMetaContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

// 홈 박스(BROWN_5)보다 한 단계 진한 베이지. 같은 갈색 계열이라 톤을 유지하면서 칩이 덩어리로 읽힌다.
const StudyLanguageChip = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  padding: 5px 8px;
  background-color: ${color.BROWN_3};
  //border: 1px solid ${color.BROWN_3};
  border-radius: ${size.BORDER_RADIUS_2};
  cursor: pointer;
`;

// 위: 현재 학습언어(국기 + 언어명) / 아래: 바꾸기 안내
const StudyLanguageTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const CurrentStudyLanguageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  padding: ${size.MARGIN_1};
  background-color: ${color.BROWN_5};
  border-radius: ${size.BORDER_RADIUS_2};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const Begin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const weekAttendanceItems = useHomeWeekAttendance();
  const consecutiveDay = useHomeConsecutiveAttendance();
  const attendanceDayCount = useHomeAttendanceDayCount();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const isSubscriber = useMySubscriptionQuery().data?.data?.status === EApiSubscriptionStatus.ACTIVE;
  const studentStatus: TStudentStatus = !isAuthenticated ? 'guest' : isSubscriber ? 'premium' : 'free';
  const studentStatusBadge = STUDENT_STATUS_BADGE[studentStatus];
  // 이미 구독 중이면 구독 페이지로 보낼 이유가 없어 배지를 표시 전용으로 둔다.
  const isSubscribable = studentStatus !== 'premium';
  const todayAttendance = weekAttendanceItems.find((item) => item.isCurrent);
  const studyConfiguration = useStudyConfiguration();
  const studyLanguage = studyConfiguration?.studyLanguage ?? EApiLanguageCode.JAPANESE;
  const isEnglish = studyLanguage === EApiLanguageCode.ENGLISH;
  const worksheetQuery = useTodayWorksheetWorkbookQuery(studyLanguage);
  const worksheetCompleted = !!worksheetQuery.data?.data.studiedAt;
  const savedLevel = isEnglish ? studyConfiguration?.englishLanguageLevel : studyConfiguration?.japaneseLanguageLevel;
  const savedStudyTimeGoal = isEnglish
    ? studyConfiguration?.englishStudyTimeGoal
    : studyConfiguration?.japaneseStudyTimeGoal;
  const currentLevel = savedLevel ?? EApiLanguageLevel.BASIC;
  const currentStudyTimeGoal =
    savedStudyTimeGoal && STUDY_TIME_GOAL_RECOMMENDED_COUNTS[savedStudyTimeGoal]
      ? savedStudyTimeGoal
      : EApiStudyTimeGoal.MEDIUM;
  const recommendedCount =
    currentStudyTimeGoal && STUDY_TIME_GOAL_RECOMMENDED_COUNTS[currentStudyTimeGoal]
      ? STUDY_TIME_GOAL_RECOMMENDED_COUNTS[currentStudyTimeGoal]
      : STUDY_TIME_GOAL_RECOMMENDED_COUNTS[EApiStudyTimeGoal.MEDIUM];
  const route = getStudyRoute(studyLanguage, currentLevel);

  const handleStepClick = (step: IStudyRouteStep) => {
    if (step.path === IYAGIGO_PATH.CLASSROOM_HOME_PATH && step.classroomSubMenu) {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'classroom', subMenu: step.classroomSubMenu }));
      navigate(step.path);
      return;
    }

    if (step.clipSubMenu) {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'clip', subMenu: step.clipSubMenu }));
      navigate(step.path);
      return;
    }

    navigate(step.path);
  };

  const handleRecommendedChipClick = () => {
    dispatch(modalActions.showDialogModal({ type: 'study-plan', data: null }));
  };

  const handleSubscriptionClick = () => navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH);

  const handleStudyLanguageChipClick = () => {
    dispatch(modalActions.showDialogModal({ type: 'study-language', data: null }));
  };

  const handleWorksheetClick = () => navigate(IYAGIGO_PATH.WORKSHEET_PATH);

  const createRouteItems = (steps: IStudyRouteStep[], offsetIndex = 0) =>
    steps.map((step, index) => {
      const routeIndex = offsetIndex + index;
      const status: TRouteStepStatus = todayAttendance?.[step.attendanceCheckKey] ? 'completed' : 'available';
      const isFirst = routeIndex === 0;
      const isLast = routeIndex === route.length - 1;

      return (
        <RouteItemContainer key={uuid4()} $status={status} onClick={() => handleStepClick(step)}>
          <RouteRail>
            <RouteNode $status={status}>
              {status === 'completed' && (
                <IconBlock icon='check' design={{ icon: { size: 'small', color: color.WHITE_1, kind: 'outlined' } }} />
              )}
              {status === 'available' && (
                <IconBlock icon='play' design={{ icon: { size: 'small', color: color.GRAY_2, kind: 'filled' } }} />
              )}
            </RouteNode>
            <RouteLine $isFirst={isFirst} $isLast={isLast} />
          </RouteRail>
          <RouteContentContainer>
            <RouteTextContainer>
              <TextBlock text={step.title} design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }} />
              <TextBlock
                text={step.description}
                design={{ text: { size: 'small', color: color.GRAY_1, bold: false } }}
              />
            </RouteTextContainer>
            <IconBlock
              icon={status === 'completed' ? 'replay' : 'right'}
              design={{
                icon: {
                  size: 'medium',
                  color: status === 'completed' ? color.BROWN_1 : color.GRAY_1,
                  kind: 'outlined',
                },
                // box: { backgroundColor: 'red' },
              }}
            />
          </RouteContentContainer>
        </RouteItemContainer>
      );
    });

  const recommendedRoutes = route.slice(0, recommendedCount);
  const optionalRoutes = route.slice(recommendedCount);
  const RecommendedRouteItems = createRouteItems(recommendedRoutes);
  const OptionalRouteItems = createRouteItems(optionalRoutes, recommendedCount);

  // 기록용. 추후 홈 상단 학습 독려·출석 영역으로 재사용할 수 있다.
  return (
    <Wrapper>
      <WelcomeContainer>
        <WelcomeMetaContainer>
          <StudentStatusContainer>
            {/* 구독 페이지는 사이드탭 → 설정 → 구독으로만 갈 수 있어 동선이 길다. 미구독자에게만 홈에서 바로 보낸다. */}
            <StudentStatusBadge
              $backgroundColor={studentStatusBadge.backgroundColor}
              $clickable={isSubscribable}
              onClick={isSubscribable ? handleSubscriptionClick : undefined}>
              <StudentStatusIcon $status={studentStatus}>
                <IconTextBlock
                  icon={studentStatusBadge.icon}
                  text={studentStatusBadge.text}
                  arrangement='icon-text'
                  design={{
                    text: { size: 'small', color: studentStatusBadge.textColor, bold: true },
                    icon: { size: 'tiny', color: studentStatusBadge.textColor, kind: 'outlined' },
                    box: { gap: '5px' },
                  }}
                />
              </StudentStatusIcon>
              {isSubscribable && (
                <IconTextBlock
                  icon='right'
                  text='구독하기'
                  arrangement='text-icon'
                  design={{
                    text: { size: 'tiny', color: studentStatusBadge.textColor, bold: false },
                    icon: { size: 'tiny', color: studentStatusBadge.textColor, kind: 'outlined' },
                    box: { gap: '2px' },
                  }}
                />
              )}
            </StudentStatusBadge>
            <Gap height={'5px'} />
            {/* 방문 기록이 없는 첫 진입에는 숫자가 어색하므로 노출하지 않는다. */}
            {attendanceDayCount > 0 && (
              <TextBlock
                text={`<strong>${attendanceDayCount}번째</strong> 방문이에요`}
                design={{ text: { size: 'medium', color: color.BLACK_4, bold: false }, box: { alignX: 'left' } }}
              />
            )}
          </StudentStatusContainer>
          <StudyLanguageChip onClick={handleStudyLanguageChipClick}>
            <StudyLanguageTextContainer>
              <CurrentStudyLanguageContainer>
                <Flag language={studyLanguage} />
                <Gap width={'5px'} />
                <TextBlock
                  text={STUDY_LANGUAGE_LABELS[studyLanguage]}
                  design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
                />
              </CurrentStudyLanguageContainer>
              <Gap height={'2px'} />
              <TextBlock
                text='학습언어 바꾸기'
                design={{ text: { size: 'tiny', color: color.BROWN_2, bold: false } }}
              />
            </StudyLanguageTextContainer>
            <IconBlock icon='swap' design={{ icon: { size: 'tiny', color: color.BROWN_2, kind: 'outlined' } }} />
          </StudyLanguageChip>
        </WelcomeMetaContainer>
        <Gap height={size.MARGIN_1} />
        {/*<Gap height={'5px'} />*/}
        <OtterMessageContainer>
          <ImageBlock src={otterReadImage} alt='이야기고 Plus⁺' design={{ image: { height: '80px' } }} />
          <Callout
            text={'오늘은 어떤 걸 배워볼까요?\n딱 10분이면 돼요!'}
            direction='left'
            theme={ETheme.CUSTOM}
            custom={{
              backgroundColor: color.WHITE_1,
              textColor: color.BLACK_2,
              borderColor: color.GRAY_1,
            }}
            size='medium'
            width='fit-content'
          />
        </OtterMessageContainer>
        <Gap height={size.MARGIN_1} />
        <FireContainer>
          <IconTextBlock
            icon='fire'
            text={
              consecutiveDay > 1
                ? `<span style="color: ${color.BLACK_2}; font-weight: bold">${consecutiveDay}일째</span> 꾸준히 공부하고 있어요!`
                : `꾸준히 공부해보아요!`
            }
            arrangement='icon-text'
            design={{
              text: { size: 'small', color: color.BLACK_2, bold: false },
              icon: {
                size: 'small',
                color: consecutiveDay > 1 ? color.RED_3 : color.YELLOW_1,
                kind: 'filled',
              },
              box: { alignX: 'left', gap: '5px', width: '100%' },
            }}
          />
        </FireContainer>
        <Gap height={size.MARGIN_1} />
        <WeekStamp items={weekAttendanceItems} width='100%' />
      </WelcomeContainer>
      <Gap height={size.MARGIN_2} />
      <RouteContainer>
        <RouteHeaderContainer>
          <TextBlock
            text='오늘의 학습'
            design={{
              text: { size: 'large', color: color.BLACK_2, bold: true },
              box: { alignX: 'left' },
            }}
          />
          <IconTextBlock
            text={`${LANGUAGE_LEVEL_LABELS[currentLevel]} · ${STUDY_TIME_GOAL_LABELS[currentStudyTimeGoal]}`}
            icon='edit'
            arrangement='text-icon'
            design={{
              text: { size: 'small', color: color.GRAY_1, bold: false },
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { alignX: 'right', gap: '5px', event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleRecommendedChipClick }}
          />
        </RouteHeaderContainer>
        <Gap height={size.MARGIN_1} />
        <>
          <WorksheetCard onClick={handleWorksheetClick}>
            <WorksheetIconContainer>
              <IconBlock
                icon='notebook-pen'
                design={{
                  icon: { size: 'medium', color: color.WHITE_1, kind: 'outlined' },
                  box: {
                    width: '40px',
                    height: '40px',
                    backgroundColor: color.CYAN_2,
                    borderRadius: '50%',
                  },
                }}
              />
              {worksheetCompleted && (
                <TextBlock
                  text='학습완료'
                  design={{
                    text: { size: 'tiny', color: color.CYAN_2, bold: true },
                    box: {
                      top: '-17px',
                      left: '-5px',
                      width: '50px',
                      padding: '1px 0',
                      backgroundColor: color.WHITE_1,
                      borderWidth: size.BORDER_WIDTH_1,
                      border: true,
                      borderColor: color.CYAN_2,
                      borderRadius: size.BORDER_RADIUS_1,
                    },
                  }}
                />
              )}
            </WorksheetIconContainer>
            <WorksheetCardContent>
              <TextBlock
                text='알짜 3분 학습지'
                design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
              />
              <TextBlock
                text={'알아두면 좋은 것만 담았어요\n고민 없이 가볍게 학습해요'}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false },
                  box: { alignX: 'left', width: '100%' },
                }}
              />
            </WorksheetCardContent>
            <IconBlock
              icon={worksheetCompleted ? 'replay' : 'right'}
              design={{ icon: { size: 'medium', color: color.CYAN_3, kind: 'outlined' } }}
            />
          </WorksheetCard>
          <Gap height={size.MARGIN_2} />
        </>
        <RouteItemsContainer>
          <ChipContainer>
            <Chip
              text='추천 코스'
              size='small'
              usage='static'
              kind='outlined'
              theme={ETheme.BROWN}
              width='fit-content'
            />
          </ChipContainer>
          {RecommendedRouteItems}
          {optionalRoutes.length > 0 && (
            <>
              {/*<Gap height={size.MARGIN_1} />*/}
              <ChipContainer>
                <Chip
                  text='시간이 남는다면'
                  size='small'
                  usage='static'
                  kind='outlined'
                  theme={ETheme.GRAY}
                  width='fit-content'
                />
              </ChipContainer>
              {OptionalRouteItems}
            </>
          )}
        </RouteItemsContainer>
      </RouteContainer>
    </Wrapper>
  );
};

export default Begin;
